import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Tooltip, TooltipProps, tooltipClasses } from '@mui/material';
import { styled } from '@mui/material/styles';

import { FONT_FAMILY } from '@/shared/ui/form-fields/fieldStyles';

import FloorPlanPinIcon from './FloorPlanPinIcon';

const TOOLTIP_TEXT_STYLE: React.CSSProperties = {
  color: 'var(--Gray-100, #EBEBEE)',
  fontFamily: 'Neue Frutiger World',
  fontSize: '10px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '160%',
};

const NoFloorPlansTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`&.${tooltipClasses.popper}`]: {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  [`& .${tooltipClasses.tooltip}`]: {
    display: 'flex',
    padding: '4px 8px',
    alignItems: 'center',
    borderRadius: '4px',
    backgroundColor: 'var(--Gray-600, #807D93)',
    maxWidth: 'none',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: 'var(--Gray-600, #807D93)',
  },
});

interface Props {
  hasGeometry: boolean;
  onClick: () => void;
  disabled?: boolean;
  hasFloorPlans?: boolean;
}

const OpenFloorPlanButton: FC<Props> = ({ hasGeometry, onClick, disabled, hasFloorPlans }) => {
  const { t } = useTranslation();
  const isDisabled = disabled || hasFloorPlans === false;

  return (
    <NoFloorPlansTooltip
      title={
        hasFloorPlans === false ? (
          <span style={TOOLTIP_TEXT_STYLE}>
            {t('spaces_form.position_on_map.no_floor_plans_tooltip')}
          </span>
        ) : (
          ''
        )
      }
      placement="bottom"
      arrow
    >
      <span>
        <Button
          data-testid="open-floor-plan-button"
          variant="contained"
          onClick={onClick}
          disabled={isDisabled}
          startIcon={<FloorPlanPinIcon />}
          sx={{
            padding: '8px 24px',
            borderRadius: '12px',
            fontFamily: FONT_FAMILY,
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '150%',
            gap: '4px',
            textTransform: 'none',
            boxShadow: 'none',
            backgroundColor: 'var(--Primary-500, #8E6DE1)',
            color: 'var(--Primary-50, #F6F5FD)',
            '&:hover': {
              backgroundColor: '#7B5ACF',
              boxShadow: 'none',
            },
            '&:disabled': {
              backgroundColor: 'var(--Primary-300, #C9BEF4)',
              color: 'var(--Primary-200, #DFDBF9)',
              boxShadow: 'none',
              border: 'none',
            },
          }}
        >
          {hasGeometry
            ? t('spaces_form.position_on_map.edit_position')
            : t('spaces_form.position_on_map.open_floor_plan')}
        </Button>
      </span>
    </NoFloorPlansTooltip>
  );
};

export default OpenFloorPlanButton;
